import { uniq } from "lodash-es";
import { OrderStatus, OrderType } from "../common/companyOrderTypes";
import {
  RegistryEventType,
  type EventRowType,
  type RegistryEventAllocationKind,
} from "./transactionLogTypes";

/**
 * One choice of the combined type filter. Order and event types share names (`CANCEL`), so each
 * value carries its kind.
 */
export type TypeFilterValue = `order:${OrderType}` | `event:${EventRowType}`;

const ALLOCATION_TRANSFER_TYPES: Record<
  RegistryEventAllocationKind,
  RegistryEventType
> = {
  ALLOCATION: RegistryEventType.OUTGOING_TRANSFER,
  RETURN: RegistryEventType.INCOMING_TRANSFER,
};

/**
 * Client-side filters of the Transactions table. The date range is not here: it is sent to the
 * backend, as are the registry event types behind the event types picked in `types`.
 *
 * @property types - Empty shows every row kind; otherwise only the picked order and event row types
 * @property portfolios - Order source portfolio names, and the portfolios allocated or returned transfers landed in
 */
export interface TransactionFilters {
  companyName: string;
  types: TypeFilterValue[];
  portfolios: string[];
  starter: string;
  statuses: OrderStatus[];
}

export const IN_PROGRESS_STATUSES: OrderStatus[] = [
  OrderStatus.PENDING,
  OrderStatus.PENDING_AGENT,
  OrderStatus.PROCESSING,
];

export const emptyTransactionFilters = (): TransactionFilters => ({
  companyName: "",
  types: [],
  portfolios: [],
  starter: "",
  statuses: [],
});

export const orderTypeFilterValue = (type: OrderType): TypeFilterValue =>
  `order:${type}`;

export const eventTypeFilterValue = (type: EventRowType): TypeFilterValue =>
  `event:${type}`;

export const selectedOrderTypes = (filters: TransactionFilters): OrderType[] =>
  filters.types
    .filter((value) => value.startsWith("order:"))
    .map((value) => value.slice("order:".length) as OrderType);

export const selectedEventRowTypes = (
  filters: TransactionFilters
): EventRowType[] =>
  filters.types
    .filter((value) => value.startsWith("event:"))
    .map((value) => value.slice("event:".length) as EventRowType);

/**
 * The registry event types to request from the backend. It knows only registry types, so a picked
 * allocation or return requests its transfer type and the rows are narrowed to the kind client-side.
 */
export const selectedEventTypes = (
  filters: TransactionFilters
): RegistryEventType[] =>
  uniq(
    selectedEventRowTypes(filters).map((type) =>
      type === "ALLOCATION" || type === "RETURN"
        ? ALLOCATION_TRANSFER_TYPES[type]
        : type
    )
  );

/**
 * Event rows have no starter or status, so either filter hides them, as does a type filter that
 * picks order types only.
 */
export const areEventRowsVisible = (filters: TransactionFilters): boolean =>
  filters.starter.trim() === "" &&
  filters.statuses.length === 0 &&
  (filters.types.length === 0 || selectedEventRowTypes(filters).length > 0);
